/* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue */
import { useCallback, useRef } from 'react';
import { AutoBreak, Common } from 'ontraccr-common';
import { DateTime } from 'luxon';

import Analytics from '../../../../../helpers/Analytics';

import { copyAutoBreakStateToNewUsers } from './useAutoBreak.helpers';

const splitEntriesByUserIdAndDate = (allTasks = []) => (
  allTasks.reduce((acc, task) => {
    if (!task) return acc;
    const { userId, date } = task;
    if (!(userId in acc)) acc[userId] = {};
    if (!(date in acc[userId])) acc[userId][date] = [];
    acc[userId][date].push(task);
    return acc;
  }, {})
);

export default ({
  autoBreakEnabled,
  settings,
  usersWithAutoBreak = new Set(),
}) => {
  /*
    Used to track created auto breaks for a user
    Prevents duplication of auto breaks on
    subsequent execution
  */
  const deletedAutoBreaksPerUser = useRef({});

  /*
    When a user toggles exclude from auto break on
    we need to add back the auto break value to get
    the original duration
  */
  const editedTasksPerUser = useRef({});

  const resetAutoBreakState = useCallback(() => {
    deletedAutoBreaksPerUser.current = {};
    editedTasksPerUser.current = {};
  }, []);

  const calculateAutoBreak = useCallback(async (allTasks, {
    userIdsToRecompute = new Set(),
    datesToRecompute = new Set(),
    timesChangedEntries = [], // Tracks which entries had their times changed
    updatedTaskIds = [], // Tracks which entries were updated
    deletedTask, // Tracks which entry was deleted
    newUsers,
    oldIdToNewUserIdMap,
    copiedFromUserId,
  } = {}) => {
    if (!autoBreakEnabled || !usersWithAutoBreak?.size) return allTasks;
    /*
      Autobreak calls getEntriesDiff
      which users entryIsNew = (e) => typeof e.id !== 'string'
      to determine if an entry is new or not.
      Entries in table will have numberic ids so they
      always show up in tasksToAdd

      Need to cast to string
    */

    const tasksWithStringIds = allTasks.map((t) => ({
      ...t,
      id: t?.id?.toString(),
    }));

    const tasksByUser = splitEntriesByUserIdAndDate(tasksWithStringIds);
    const userIds = Object.keys(tasksByUser);

    const fullTaskMap = Common.getIdMap(tasksWithStringIds);

    let updatedDeletedAutoBreaks = deletedAutoBreaksPerUser.current;
    let updatedEditedTasksPerUser = editedTasksPerUser.current;

    if (newUsers?.length) {
      const {
        deletedAutoBreaksPerUser: newDeletedAutoBreaks,
        editedTasksPerUser: newEditedTasksPerUser,
        tasksToAdjust,
      } = copyAutoBreakStateToNewUsers({
        newUsers,
        copiedDates: datesToRecompute,
        oldIdToNewUserIdMap,
        copiedFromUserId,
        deletedAutoBreaksPerUser: deletedAutoBreaksPerUser.current,
        editedTasksPerUser: editedTasksPerUser.current,
        usersWithAutoBreak,
        allTasks: tasksWithStringIds,
        autoBreakDuration: settings?.autoBreakDuration,
      });
      updatedDeletedAutoBreaks = newDeletedAutoBreaks;
      updatedEditedTasksPerUser = newEditedTasksPerUser;

      // Apply adjustments to tasks that were copied to users without auto break
      tasksToAdjust.forEach((adjustedTask) => {
        if (adjustedTask.id in fullTaskMap) {
          fullTaskMap[adjustedTask.id] = {
            ...fullTaskMap[adjustedTask.id],
            ...adjustedTask,
          };
        }
      });
    }

    const newDeletedAutoBreaks = {};
    const newEditedTasksPerUser = Object.keys(updatedEditedTasksPerUser).reduce((acc, userId) => {
      acc[userId] = new Set(updatedEditedTasksPerUser[userId]);
      return acc;
    }, {});

    if (timesChangedEntries?.length) {
      timesChangedEntries.forEach((timesChangedEntry) => {
        newEditedTasksPerUser[timesChangedEntry.userId]?.delete(timesChangedEntry.id);
      });
    }

    for (const ourUserId of userIds) {
      if (!usersWithAutoBreak.has(ourUserId) || !userIdsToRecompute.has(ourUserId)) {
        newDeletedAutoBreaks[ourUserId] = [...(updatedDeletedAutoBreaks[ourUserId] ?? [])];
        newEditedTasksPerUser[ourUserId] = updatedEditedTasksPerUser[ourUserId]
          ? new Set(updatedEditedTasksPerUser[ourUserId])
          : new Set();
        continue;
      }
      const userTaskMap = tasksByUser[ourUserId];
      let deletedAutoBreaks = [...(updatedDeletedAutoBreaks[ourUserId] ?? [])];
      const changedTaskSet = new Set(updatedEditedTasksPerUser[ourUserId] ?? []);
      if (deletedTask) {
        if (changedTaskSet.has(deletedTask.id)) {
          deletedAutoBreaks = deletedAutoBreaks.filter((deletedBreak) => (
            deletedBreak.date !== deletedTask.date
          ));
        }
        changedTaskSet.delete(deletedTask.id);
      }
      for (const date of Object.keys(userTaskMap)) {
        if (!datesToRecompute.has(date)) continue;
        let userTasks = userTaskMap[date] ?? [];

        const {
          autoBreakDuration = 15,
        } = settings ?? {};

        userTasks = userTasks.map((t) => {
          if (!changedTaskSet.has(t.id) || updatedTaskIds?.includes(t.id)) return t;
          const newT = {
            ...t,
            endTime: t.endTime + (autoBreakDuration * 60 * 1000),
          };
          fullTaskMap[t.id] = {
            ...fullTaskMap[t.id] ?? {},
            ...newT,
          };
          newEditedTasksPerUser[ourUserId].delete(t.id);
          return newT;
        });
        deletedAutoBreaks = deletedAutoBreaks.filter((deletedBreak) => (
          !datesToRecompute.has(deletedBreak.date)
        ));

        const userAutoBreakDiff = await AutoBreak({
          settings: {
            ...settings,
            autoBreakPlacement: 'End', // Always use End
          },
          tasks: userTasks,
          /*
            Already checking perms in usersWithAutoBreak
            so anyone that gets here has the perms
          */
          userPermissions: new Set(['AUTOMATIC_BREAKS']),
          notifyError: () => {
          },
          day: DateTime.fromSQL(date),
          askShouldDelete: false,
          deletedAutoBreaks,
          Analytics,
        });
        if (!userAutoBreakDiff) {
          continue;
        }
        const {
          tasksToEdit = [],
          tasksToAdd = [],
        } = userAutoBreakDiff;
        tasksToEdit.forEach((editedTask) => {
          if (!editedTask || !(editedTask.id in fullTaskMap)) return;
          if (!(ourUserId in newEditedTasksPerUser)) {
            newEditedTasksPerUser[ourUserId] = new Set();
          }
          newEditedTasksPerUser[ourUserId].add(editedTask.id);
          fullTaskMap[editedTask.id] = {
            ...fullTaskMap[editedTask.id] ?? {},
            ...editedTask,
          };
        });
        newDeletedAutoBreaks[ourUserId] = [
          ...newDeletedAutoBreaks[ourUserId] ?? [],
          ...tasksToAdd.filter((t) => t.type === 'break'),
        ];
      }
    }
    deletedAutoBreaksPerUser.current = newDeletedAutoBreaks;
    editedTasksPerUser.current = newEditedTasksPerUser;
    return Object.values(fullTaskMap);
  }, [
    autoBreakEnabled,
    settings,
    usersWithAutoBreak,
  ]);
  return {
    calculateAutoBreak,
    resetAutoBreakState,
  };
};
